import type {useSoftAssertFailed} from '../../context';
import type {ExternalAccount} from './ExternalAccountConfirmation';

const _getExternalAccountForVerificationWithID = (
  externalAccounts: ReadonlyArray<ExternalAccount>,
  targetID: string,
  softAssertFailed: ReturnType<typeof useSoftAssertFailed>,
): ExternalAccount | undefined => {
  const externalAccount = externalAccounts.find(({id}) => id === targetID);
  if (!externalAccount) {
    softAssertFailed(
      'Could not find external account for verification with given externalAccountId',
      'connected_accounts_ui',
    );
  }

  if (externalAccount && externalAccount.object !== 'blockchain_address') {
    return externalAccount;
  }

  // If the target is a blockchain address, then we will return the first external account that is not a blockchain address
  // eslint-disable-next-line no-restricted-syntax
  for (const ea of externalAccounts) {
    if (ea.object !== 'blockchain_address') {
      return ea;
    }
  }

  // If all external accounts are blockchain addresses, we return the one with targetID
  return externalAccount;
};

// We grab an external account for verification, preferring bank accounts or debit cards over blockchain addresses
export const getExternalAccountForVerification = (
  externalAccounts: ReadonlyArray<ExternalAccount>,
  softAssertFailed: ReturnType<typeof useSoftAssertFailed>,
  defaultCurrency: string,
  targetID?: string | null,
): ExternalAccount | undefined => {
  if (targetID) {
    return _getExternalAccountForVerificationWithID(
      externalAccounts,
      targetID,
      softAssertFailed,
    );
  }

  let firstNonBlockchainAddressAccount = null;

  // First try to return the default_for_currency account in the default currency, if it isn't a blockchain address
  // Otherwise try to return the first non-blockchain address external account
  // Return first non-financial account, there will be at least one if we are showing this form
  // eslint-disable-next-line no-restricted-syntax
  for (const ea of externalAccounts) {
    if (ea.object !== 'blockchain_address') {
      if (ea.default_for_currency && ea.currency === defaultCurrency) {
        // Cards don't have financial account prop so we return it directly
        // Return first external account that's not a financial account
        if (!('financial_account' in ea) || ea.financial_account === null) {
          return ea;
        }
      }

      if (!firstNonBlockchainAddressAccount) {
        firstNonBlockchainAddressAccount = ea;
      }
    }
  }

  // Try to return the first non blockchain address account, if it exists, otherwise we just return the first external account
  return firstNonBlockchainAddressAccount || externalAccounts[0];
};
